const MAX_WAIT_FRAMES = 600;
const FALLBACK_GRADIENT_START = [255, 168, 1];
const FALLBACK_GRADIENT_END = [235, 21, 70];
const CHARACTER_DURATION = 0.16;
const CHARACTER_STAGGER = 0.028;
const DESCRIPTION_DURATION = 0.32;

/**
 * Highlights the heading character-by-character, then reveals the description.
 * Playback is time-based and restarts each time the section enters the viewport.
 */
class GardenerInstinct extends HTMLElement {
  #controller;
  #matchMedia;
  #splits = [];
  #setupFrame;
  #paintFrame;
  #waitAttempts = 0;

  connectedCallback() {
    if (this.#controller) return;

    this.#restoreStaticState();
    this.#controller = new AbortController();
    this.#waitAttempts = 0;
    window.addEventListener('pagehide', this.#onPageHide, { signal: this.#controller.signal });
    window.addEventListener('pageshow', this.#onPageShow, { signal: this.#controller.signal });
    this.#waitForGsap();
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#controller = undefined;

    if (this.#setupFrame) cancelAnimationFrame(this.#setupFrame);
    this.#setupFrame = undefined;
    this.#cancelPaint();
    this.#destroyMotion();
  }

  #waitForGsap = () => {
    if (!this.#controller || this.#controller.signal.aborted) return;
    if (this.#matchMedia) return;

    const gsap = window.gsap;

    if (gsap) {
      this.#setupFrame = undefined;
      this.#setupMotion(gsap);
      return;
    }

    this.#waitAttempts += 1;
    if (this.#waitAttempts >= MAX_WAIT_FRAMES) return;

    this.#setupFrame = requestAnimationFrame(this.#waitForGsap);
  };

  #onPageHide = () => {
    this.#destroyMotion();
  };

  #onPageShow = (event) => {
    if (!event.persisted) return;

    this.#restoreStaticState();
    this.#waitAttempts = 0;
    this.#waitForGsap();
  };

  /**
   * @param {typeof window.gsap} gsap
   */
  #setupMotion(gsap) {
    const title = this.querySelector('[data-gardener-instinct-title]');
    const description = this.querySelector('[data-gardener-instinct-description]');
    const lines = [...this.querySelectorAll('[data-gardener-instinct-line]')];

    if (!title || !lines.length) return;

    this.#destroyMotion();

    if (window.SplitText) gsap.registerPlugin(window.SplitText);

    this.#matchMedia = gsap.matchMedia();
    this.#matchMedia.add(
      {
        reduceMotion: '(prefers-reduced-motion: reduce)',
        allowMotion: '(prefers-reduced-motion: no-preference)'
      },
      (context) => {
        const { reduceMotion } = context.conditions;

        if (reduceMotion) {
          if (description) gsap.set(description, { clearProps: 'all' });
          this.removeAttribute('data-enhanced');
          return undefined;
        }

        const localSplits = lines.map((line) => this.#splitLine(line));
        const lineCharacters = localSplits.map((split) => split.characters);
        const characters = lineCharacters.flat();

        if (!characters.length) {
          localSplits.forEach((split) => split.revert());
          lines.forEach((line) => line.removeAttribute('aria-hidden'));
          return undefined;
        }

        this.#splits = localSplits;
        this.setAttribute('data-enhanced', '');
        this.#paintGradient(title, characters);

        gsap.set(characters, { opacity: 0.2 });
        if (description) gsap.set(description, { autoAlpha: 0 });

        const timeline = gsap.timeline({
          paused: true,
          defaults: { ease: 'none', overwrite: 'auto' }
        });

        lineCharacters.forEach((charactersInLine) => {
          if (!charactersInLine.length) return;

          timeline.fromTo(
            charactersInLine,
            { opacity: 0.2 },
            {
              opacity: 1,
              duration: CHARACTER_DURATION,
              stagger: { each: CHARACTER_STAGGER, from: 'start' },
              immediateRender: false
            }
          );
        });

        if (description) {
          timeline.fromTo(
            description,
            { autoAlpha: 0 },
            {
              autoAlpha: 1,
              duration: DESCRIPTION_DURATION,
              ease: 'power2.out',
              immediateRender: false
            }
          );
        }

        let isActive = true;
        let isVisible = false;

        const showIdleState = () => {
          timeline.pause(0);
        };

        const playTimeline = () => {
          if (!isActive) return;
          timeline.restart();
        };

        let observer;
        if ('IntersectionObserver' in window) {
          observer = new IntersectionObserver(
            (entries) => {
              if (!isActive) return;

              const isIntersecting = entries.some((entry) => entry.isIntersecting);
              if (isIntersecting === isVisible) return;
              isVisible = isIntersecting;

              if (isIntersecting) {
                playTimeline();
                return;
              }

              showIdleState();
            },
            { threshold: 0.28 }
          );
          observer.observe(this);
        } else {
          playTimeline();
        }

        const resizeObserver = typeof ResizeObserver === 'function'
          ? new ResizeObserver(() => this.#schedulePaint(title, characters))
          : undefined;
        resizeObserver?.observe(title);

        document.fonts?.ready.then(() => {
          if (!this.isConnected || !this.#matchMedia) return;
          this.#paintGradient(title, characters);
        });

        return () => {
          isActive = false;
          observer?.disconnect();
          resizeObserver?.disconnect();
          timeline.kill();
          localSplits.forEach((split) => split.revert());
          this.#splits = [];
          lines.forEach((line) => line.removeAttribute('aria-hidden'));
          if (description) gsap.set(description, { clearProps: 'all' });
          this.removeAttribute('data-enhanced');
        };
      },
      this
    );
  }

  #splitLine(line) {
    line.setAttribute('aria-hidden', 'true');

    if (window.SplitText?.create) {
      const split = window.SplitText.create(line, {
        type: 'words,chars',
        tag: 'span',
        aria: 'none',
        charsClass: 'gardener-instinct__char',
        wordsClass: 'gardener-instinct__word'
      });

      return {
        characters: split.chars,
        revert: () => split.revert()
      };
    }

    const text = line.textContent || '';
    const fragment = document.createDocumentFragment();
    const characters = [];

    text.split(/(\s+)/).forEach((part) => {
      if (!part) return;
      if (/^\s+$/.test(part)) {
        fragment.append(document.createTextNode(part));
        return;
      }

      const word = document.createElement('span');
      word.className = 'gardener-instinct__word';

      [...part].forEach((character) => {
        const element = document.createElement('span');
        element.className = 'gardener-instinct__char';
        element.textContent = character;
        word.append(element);
        characters.push(element);
      });

      fragment.append(word);
    });

    line.replaceChildren(fragment);

    return {
      characters,
      revert: () => {
        line.textContent = text;
      }
    };
  }

  /**
   * Samples the section gradient per character using its horizontal position inside the
   * title box, so the split heading matches the `background-clip: text` fallback exactly.
   * @param {HTMLElement} title - Heading element that defines the gradient box.
   * @param {HTMLElement[]} characters - Character elements produced by the split.
   */
  #paintGradient(title, characters) {
    if (!characters.length) return;

    const styles = getComputedStyle(this);
    const start = this.#parseColor(styles.getPropertyValue('--gardener-instinct-gradient-start'), FALLBACK_GRADIENT_START);
    const end = this.#parseColor(styles.getPropertyValue('--gardener-instinct-gradient-end'), FALLBACK_GRADIENT_END);
    const titleRect = title.getBoundingClientRect();

    if (!titleRect.width) return;

    const progressValues = characters.map((character) => {
      const rect = character.getBoundingClientRect();
      const center = rect.left + (rect.width / 2) - titleRect.left;
      return Math.min(Math.max(center / titleRect.width, 0), 1);
    });

    progressValues.forEach((progress, index) => {
      const color = start.map((channel, offset) => Math.round(channel + ((end[offset] - channel) * progress)));
      characters[index].style.setProperty('--gardener-instinct-char-color', color.join(' '));
    });
  }

  /**
   * @param {HTMLElement} title
   * @param {HTMLElement[]} characters
   */
  #schedulePaint(title, characters) {
    this.#cancelPaint();
    this.#paintFrame = requestAnimationFrame(() => {
      this.#paintFrame = undefined;
      this.#paintGradient(title, characters);
    });
  }

  #cancelPaint() {
    if (!this.#paintFrame) return;
    cancelAnimationFrame(this.#paintFrame);
    this.#paintFrame = undefined;
  }

  #parseColor(value, fallback) {
    const input = String(value || '').trim();
    const hexMatch = input.match(/^#?([\da-f]{3}|[\da-f]{6})$/i);

    if (hexMatch) {
      const hex = hexMatch[1];
      const expanded = hex.length === 3 ? hex.replace(/./g, (character) => character + character) : hex;
      return [0, 2, 4].map((offset) => parseInt(expanded.slice(offset, offset + 2), 16));
    }

    const rgbMatch = input.match(/^rgba?\(([^)]+)\)$/i);
    if (rgbMatch) {
      const channels = rgbMatch[1].split(/[\s,/]+/).filter(Boolean).slice(0, 3).map(Number);
      if (channels.length === 3 && channels.every((channel) => Number.isFinite(channel))) return channels;
    }

    return fallback;
  }

  #destroyMotion() {
    this.#cancelPaint();
    this.#matchMedia?.revert();
    this.#matchMedia = undefined;

    this.#splits.forEach((split) => split.revert());
    this.#splits = [];
    this.querySelectorAll('[data-gardener-instinct-line]').forEach((line) => line.removeAttribute('aria-hidden'));
    this.#restoreStaticState();
  }

  #restoreStaticState() {
    this.removeAttribute('data-enhanced');
    this.querySelector('[data-gardener-instinct-stage]')?.removeAttribute('style');
    this.querySelector('[data-gardener-instinct-description]')?.removeAttribute('style');
  }
}

if (!customElements.get('gardener-instinct')) {
  customElements.define('gardener-instinct', GardenerInstinct);
}
